import { useEffect, useState, type MouseEvent } from 'react';
import {
  notifEvents,
  notifEventsSave,
  notifPrefs,
  notifPrefsSave,
  pushDeviceRemove,
  pushDevices,
  pushDeviceTest,
  userAlertPhone,
  userAlertPhoneSet,
  userAlertPhoneVerify,
  userAlertPhoneChangeRequest,
  userAlertPhoneTest,
  meConnectionsConsent,
  type ApiError,
  type NotifChannel,
  type NotifEventChoice,
  type PushDevice,
} from '@aithora/shared';
import { pushTokenHash, registerForPush, registeredPushToken } from '../../session/push';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { SmsConsentBox } from './SmsConsentBox';
import { ErrLine, I, chip, maskPhone, notServed, pre, sx, useApi, useGuarded, usePage } from './kit';
import { useCustomerText } from '../../ui/buildLabels';

const GROUPS: [NotifEventChoice['severity'], string, string][] = [
  ['Critical', '#B3261E', 'rgba(179,38,30,.14)'], ['High', '#5B6B7C', 'rgba(91,107,124,.18)'], ['Medium', '#264C7E', 'rgba(79,138,214,.16)'], ['Info', '#54606C', 'rgba(34,39,46,.08)'],
];
const tone = (ok: string) => (ok === 'ok' ? '#1F6B52' : ok === 'warn' ? '#A5561A' : '#B3261E');
const actStyle = (primary?: boolean) =>
  'display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' +
  (primary ? 'background:#2F5C97;color:#fff;' : 'background:#F7F3EA;color:#22272E;border:1px solid rgba(34,39,46,.2);');
const ago = (iso: string | null) => {
  if (!iso) return 'never';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? 'just now' : m < 60 ? m + ' min ago' : m < 1440 ? Math.round(m / 60) + 'h ago' : Math.round(m / 1440) + ' days ago';
};
const KIND: Record<string, string> = {
  ios: 'Phone · app push', android: 'Phone · app push', ipad: 'Tablet · app push', web: 'Desktop · browser push', web_ios: 'Tablet · web console',
};

/** Devices registered before they carried a name. */
const UNNAMED: Record<string, string> = { web: 'A browser (no name)', android: 'Android phone', ios: 'iPhone', ipad: 'iPad' };

/** Where alerts reach you, one card per kind of channel (Andrew, 2026-10-03). */
type DeviceGroup = 'browser' | 'phone' | 'sms';
const CARDS: Array<[DeviceGroup, string, string]> = [
  ['browser', 'Browsers', 'No other browser is signed in for alerts.'],
  ['phone', 'Phone app', 'No phone yet. Sign in to the Aithora app on your phone and allow notifications.'],
  ['sms', 'Text messages', ''],
];
const groupOf = (platform: string): DeviceGroup => (platform === 'web' ? 'browser' : 'phone');

interface DeviceRow {
  id: string;
  group: DeviceGroup;
  name: string;
  kind: string;
  ok: 'ok' | 'warn' | 'bad';
  status: string;
  acts: { label: string; fn: () => void; primary?: boolean }[];
  done?: string;
}

/** Notifications (design seIsNotifs, T9860; EVENTS L21982-22038, seDevices L22854, L22886-22992). */
export function NotificationsSection() {
  const plain = useCustomerText();
  const { client, session } = useSession();
  const { lens, changed } = usePage();
  const { canWrite, roReason, wr } = useGuarded();
  const me = session?.user.id ?? '';
  const events = useApi((c, s) => notifEvents(c, { signal: s }), []);
  const devices = useApi((c, s) => pushDevices(c, { signal: s }), []);
  const phone = useApi((c, s) => userAlertPhone(c, { params: { id: me }, signal: s }), [me], !!me);
  const prefs = useApi((c, s) => notifPrefs(c, { signal: s }), []);
  const [perm, setPerm] = useState<string>(() => (typeof Notification === 'undefined' ? 'unsupported' : Notification.permission));
  const [tested, setTested] = useState<Record<string, string>>({});
  // This browser's own row is the "This computer" card, so the list leaves it out.
  const [ownHash, setOwnHash] = useState<string | null>(null);
  useEffect(() => {
    const t = registeredPushToken();
    if (t) void pushTokenHash(t).then(setOwnHash);
  }, [devices.data]);
  const [asked, setAsked] = useState(false);
  const [newPhone, setNewPhone] = useState<string | null>(null);
  const [code, setCode] = useState('');
  // The consent box beside a number being added or verified: unchecked by default,
  // sent with the code (recorded once the number is proved to be yours).
  const [agree, setAgree] = useState(false);
  // The server's word when a code was NOT texted (live:false, e.g. Twilio not set up): shown
  // in place of the code box, which would otherwise read as if a text went out.
  const [unsent, setUnsent] = useState<string | null>(null);
  const [needConsent, setNeedConsent] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  const operator = lens.lens === 'Operator';
  const smsLocked = !operator;
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  if (!events.data && !events.error) return <Skeleton rows={8} label="Loading your alerts" />;

  // ---- where alerts reach you ----
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const thisName = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'This browser';
  const fbMin = prefs.data?.smsFallbackMinutes ?? 5;
  const iosCritical = devices.data?.delivery.iosCriticalAlerts.live === true;
  const rows: DeviceRow[] = [{
    id: 'this', group: 'browser', name: 'This computer · ' + thisName, kind: 'Desktop · browser push, works with the tab closed',
    ok: perm === 'granted' ? 'ok' : perm === 'denied' ? 'bad' : 'warn',
    status: perm === 'granted' ? 'Push allowed' + (tested.this ? ' · test sent ' + tested.this : '')
      : perm === 'denied' ? 'Blocked in this browser. Allow it from the lock icon in the address bar.'
      : perm === 'unsupported' ? 'This browser cannot receive push. Email and SMS still reach you.' : 'Not allowed yet',
    acts: perm === 'granted'
      ? [{ label: 'Send test', fn: () => { try { new Notification('Aithora test alert', { body: 'Push works on this computer.' }); } catch { /* blocked */ } setTested((p) => ({ ...p, this: 'just now' })); } }]
      : perm === 'default' ? [{ label: 'Allow push', primary: true, fn: () => { void registerForPush(client, { prompt: true }).then(() => { setPerm(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission); devices.reload(); }); } }] : [],
  }];
  (devices.data?.devices ?? []).filter((d: PushDevice) => !(ownHash && d.tokenHash === ownHash)).forEach((d: PushDevice) => {
    const warn = d.status === 'not_on_home_screen';
    const failing = d.status === 'failing' || d.status === 'disabled';
    rows.push({
      id: d.id,
      group: groupOf(d.platform),
      name: d.name ?? UNNAMED[d.platform] ?? d.platform,
      kind: KIND[d.platform] ?? d.platform,
      ok: failing ? 'bad' : warn ? 'warn' : 'ok',
      status: warn
        ? 'Not on the Home Screen, so push cannot reach it. Share → Add to Home Screen, then open it once.'
        : failing ? 'Pushes to this device are failing' + (d.lastError ? ': ' + d.lastError : '.')
        : 'Last delivered ' + (tested[d.id] ?? ago(d.lastDeliveredAt)) + (d.platform === 'ios' && iosCritical ? ' · Critical alerts break through silent mode' : ''),
      acts: [
        ...(warn ? [] : [{ label: 'Send test', fn: () => void run(async () => { const r = await pushDeviceTest(client, { params: { id: d.id }, body: {} }); if (!r.live || r.delivered === false) throw Object.assign(new Error(r.reason ?? r.error ?? 'Not delivered'), { message: r.reason ?? r.error ?? 'Not delivered' }); setTested((p) => ({ ...p, [d.id]: 'just now' })); }) }]),
        { label: 'Remove', fn: () => void run(async () => { await pushDeviceRemove(client, { params: { id: d.id } }); devices.reload(); }) },
      ],
    });
  });
  const ph = phone.data;
  const number = ph?.phone ?? null;
  // Texts and calls need consent (AF-1566): it only counts for the number on file once verified.
  const consented = !!(number && ph?.verified && ph?.consent?.onFile);
  // SMS on an alert is only real with a verified number that has consent; otherwise the
  // channel shows as unavailable on every event row (never as on).
  const smsOff: string | null = !ph ? null : !(number && ph.verified) ? 'No verified number: SMS is unavailable' : !consented ? 'No text consent on file: SMS is unavailable' : null;
  rows.push({
    id: 'sms',
    group: 'sms',
    name: number ? maskPhone(number) : 'No number yet',
    kind: 'Text message via Twilio' + (number ? (ph?.verified ? ' · verified' : ' · not verified yet') : ''),
    ok: number && ph?.verified && consented ? 'ok' : 'warn',
    status: (number && ph?.verified && !consented ? 'No texts or calls go to this number until you tick the box below. Push and in-app still do. ' : '') +
      (smsLocked ? (number ? 'Number set by your administrator. Only they can change it.' : 'Your administrator sets your alert number.') : number ? 'Backup for any push not opened in ' + fbMin + ' min' + (tested.sms ? ' · test sent ' + tested.sms : '') : 'Add a number to get the SMS backup.'),
    acts: smsLocked
      ? (asked ? [] : [{ label: 'Ask my admin to change', fn: () => void run(async () => { await userAlertPhoneChangeRequest(client, { params: { id: me }, body: {} }); setAsked(true); }) }])
      : [...(number ? [{ label: 'Send test', fn: () => void run(async () => { await userAlertPhoneTest(client, { params: { id: me }, body: {} }); setTested((p) => ({ ...p, sms: 'just now' })); }) }] : []), { label: number ? 'Change' : 'Add number', fn: () => { setUnsent(null); setNewPhone(''); } }],
    done: smsLocked && asked ? 'Request sent to your administrator' : '',
  });

  // ---- events ----
  const list = events.data?.events ?? [];
  const save = (e: NotifEventChoice, enabled: boolean, channels: NotifChannel[]) => void run(async () => {
    events.setData((p) => (p ? { ...p, events: p.events.map((x) => (x.key === e.key ? { ...x, enabled, channels } : x)) } : p));
    try {
      await notifEventsSave(client, { body: { events: [{ eventKey: e.key, enabled, channels }] } });
    } catch (x) {
      events.reload();
      throw x;
    }
  });
  const quietOn = prefs.data?.quietHours === true;

  const note = operator
    ? 'Your own alerts. Who gets paged for the whole platform, and in what order, is set in On-call.'
    : lens.lens === 'Sales' ? 'Your own alerts: replies on your deals and meetings coming up. Everything here also shows in the bell at the top.'
    : lens.lens === 'Dev' ? 'Your own alerts: outages, sync delays and version changes across the platform. Everything here also shows in the bell at the top.'
    : lens.lens === 'Care Staff' ? 'Alerts your role receives. Critical ones are required by your building and always go to two channels, so they cannot be switched off here.'
    : 'Alerts for your building. Critical events always go to at least two channels. Which staff get which alerts by text is set per role in Users & Permissions.';

  return (
    <div data-screen-label="Notifications" data-testid="settings-notifications" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;')} />
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Notifications</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:12px;text-wrap:pretty;')}>{I(note)}</div>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:6px;')}>Where alerts reach you</div>
      <div data-testid="notif-devices" style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,250px),1fr));gap:0 22px;margin-bottom:14px;align-items:start;')}>
        {CARDS.map(([g, title, empty]) => {
          const rs = rows.filter((r) => r.group === g);
          return (
            <div key={g} data-testid={'notif-card-' + g} style={sx('min-width:0;border-top:1px solid rgba(34,39,46,.18);padding-top:8px;')}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;margin-bottom:2px;')}>
                <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(title)}</span>
                {g !== 'sms' && <span style={sx('font-size:16px;color:#54606C;')}>{I(rs.length === 1 ? '1 connected' : rs.length + ' connected')}</span>}
              </div>
              {rs.length === 0 && empty && <div style={sx('font-size:16px;color:#54606C;padding:6px 0;text-wrap:pretty;')}>{I(empty)}</div>}
              {rs.map((d) => (
          <div key={d.id} style={sx('min-width:0;display:flex;flex-direction:column;gap:4px;padding:8px 0;border-top:1px dashed rgba(34,39,46,.1);')}>
            <div style={sx('display:flex;align-items:center;gap:7px;min-width:0;')}>
              <span style={sx('width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + tone(d.ok) + ';')} />
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(d.name)}</span>
            </div>
            <div style={sx('font-size:16px;color:#54606C;')}>{I(d.kind)}</div>
            <div style={sx('font-size:16px;line-height:1.4;color:' + (d.ok === 'ok' ? '#3C454F' : tone(d.ok)) + ';text-wrap:pretty;')}>{I(d.status)}</div>
            <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:2px;')}>
              {d.acts.map((x) => (
                <a key={x.label} href="#" {...(x.label === 'Allow push' || x.label === 'Send test' && d.id === 'this' ? { onClick: (e: MouseEvent) => { pre(e); x.fn(); } } : wr(() => x.fn()))} style={sx(actStyle(x.primary))}>{I(x.label)}</a>
              ))}
              {d.done && (
                <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;')}>
                  {'✓ '}
                  {I(d.done)}
                </span>
              )}
            </div>
            {d.id === 'sms' && newPhone !== null && (
              <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:4px;')}>
                <input type="tel" aria-label="New alert number" placeholder="+15125550100" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} style={sx('flex:1 1 120px;height:26px;padding:0 8px;border:1px solid rgba(34,39,46,.2);border-radius:6px;background:#FBF8F1;font-size:16px;font-family:inherit;box-sizing:border-box;')} />
                <a
                  href="#"
                  {...wr(() => {
                    // The box must be ticked before anything is texted (AF-1566).
                    if (!agree) { setNeedConsent(true); return; }
                    setNeedConsent(false);
                    void run(async () => {
                      const r = await userAlertPhoneSet(client, { params: { id: me }, body: { phone: newPhone } });
                      setUnsent(r.live ? null : r.reason ?? 'The code could not be texted.');
                      setNewPhone(null);
                      phone.reload();
                    });
                  })}
                  aria-disabled={!agree || !canWrite || undefined}
                  style={sx(actStyle(agree))}
                >
                  Send code
                </a>
              </div>
            )}
            {d.id === 'sms' && newPhone !== null && needConsent && !agree && (
              <div role="alert" style={sx('font-size:16px;color:#B3261E;margin-top:4px;')}>{I('Tick the box below to agree to texts and calls before we send a code.')}</div>
            )}
            {d.id === 'sms' && unsent && newPhone === null && (
              <div role="status" data-testid="sms-not-sent" style={sx('font-size:16px;color:#8A2A22;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
                {I('No code was sent: ' + unsent)}
              </div>
            )}
            {d.id === 'sms' && ph?.pendingPhone && !unsent && (
              <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:4px;')}>
                <input type="text" inputMode="numeric" aria-label="Code you received" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} style={sx('flex:1 1 90px;height:26px;padding:0 8px;border:1px solid rgba(34,39,46,.2);border-radius:6px;background:#FBF8F1;font-size:16px;font-family:inherit;box-sizing:border-box;')} />
                <a href="#" onClick={(e) => { pre(e); if (code.length === 6) void run(async () => { await userAlertPhoneVerify(client, { params: { id: me }, body: { code, consent: agree, source: 'console' } }); setCode(''); setAgree(false); phone.reload(); }); }} aria-disabled={code.length !== 6 || undefined} style={sx(actStyle(code.length === 6))}>Verify</a>
              </div>
            )}
            {d.id === 'sms' && (ph?.pendingPhone || newPhone !== null) && <SmsConsentBox role={session?.user.rawRole} checked={agree} onChange={setAgree} />}
            {d.id === 'sms' && !ph?.pendingPhone && newPhone === null && number && ph?.verified && (
              <SmsConsentBox
                role={session?.user.rawRole}
                checked={consented}
                onChange={(on) => void run(async () => { await meConnectionsConsent(client, { body: { granted: on, source: 'console' } }); phone.reload(); })}
              />
            )}
          </div>
              ))}
            </div>
          );
        })}
      </div>
      <ErrLine error={err} style="margin:-6px 0 10px;" />
      {events.error && <ErrLine error={events.error} style="margin:-6px 0 10px;" />}
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,400px),1fr));gap:0 30px;align-items:start;')}>
        {GROUPS.map(([sev, fg, bg]) => {
          const evs = list.filter((e) => e.severity === sev);
          return (
            <div key={sev} style={sx('margin-bottom:15px;')}>
              <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:8px;')}>
                <span style={sx(chip(fg, bg))}>{I(sev)}</span>
                <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
                <span style={sx('font-size:16px;color:#54606C;white-space:nowrap;')}>{I(evs.filter((e) => e.required || e.enabled).length + ' of ' + evs.length + ' on')}</span>
              </div>
              {evs.map((e) => {
                const on = e.required ? true : e.enabled;
                const ch = e.channels;
                return (
                  <div key={e.key} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:7px;padding:5px 0;border-top:1px solid rgba(34,39,46,.06);')}>
                    <a
                      href="#"
                      role="switch"
                      aria-checked={on}
                      aria-label={e.label}
                      aria-disabled={e.required || !canWrite || undefined}
                      title={roReason ?? (e.required ? 'Required for your role.' : undefined)}
                      onClick={(x) => { pre(x); if (!e.required && canWrite) save(e, !on, ch); }}
                      style={sx('display:inline-flex;align-items:center;width:36px;height:20px;flex:0 0 36px;border-radius:11px;padding:2px;text-decoration:none;box-sizing:border-box;' + (on ? 'background:#2F5C97;justify-content:flex-end;' : 'background:rgba(34,39,46,.16);justify-content:flex-start;'))}
                    >
                      <span style={sx('display:block;width:16px;height:16px;border-radius:50%;background:#FCFAF4;')} />
                    </a>
                    <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:16px;')}>
                      {I(e.label)}
                      {sev === 'Critical' && <span style={sx('display:block;font-size:16px;color:#8A2A22;font-weight:600;margin-top:1px;')}>{I(e.required ? 'Required · 2+ channels' : '2+ channels')}</span>}
                    </span>
                    {(['push', 'sms', 'email'] as NotifChannel[]).map((c) => {
                      const off = c === 'sms' ? smsOff : null;
                      const has = ch.includes(c) && !off;
                      return (
                        <a
                          key={c}
                          href="#"
                          aria-pressed={has}
                          aria-label={e.label + ' by ' + c + (off ? ' (unavailable)' : '')}
                          aria-disabled={!on || !canWrite || !!off || undefined}
                          title={off ?? roReason ?? undefined}
                          onClick={(x) => {
                            pre(x);
                            if (!on || !canWrite || off) return;
                            if (has && ch.length <= e.minChannels) return;
                            save(e, on, has ? ch.filter((y) => y !== c) : [...ch, c]);
                          }}
                          style={sx('display:inline-flex;align-items:center;height:22px;padding:0 9px;border-radius:6px;font-size:16px;text-decoration:none;white-space:nowrap;' + (!on || off ? 'background:rgba(34,39,46,.05);color:#54606C;border:1px solid rgba(34,39,46,.1);' : has ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#F7F3EA;color:#3C454F;border:1px solid rgba(34,39,46,.14);') + (off ? 'text-decoration:line-through;' : ''))}
                        >
                          {I(c === 'sms' ? (off ? 'SMS unavailable' : 'SMS') : c)}
                        </a>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:7px;padding-top:13px;border-top:1px solid rgba(34,39,46,.1);')}>
        <a
          href="#"
          role="switch"
          aria-checked={quietOn}
          aria-label="Quiet hours"
          {...wr(() => { if (prefs.data) void run(async () => { await notifPrefsSave(client, { body: { quietHours: !quietOn } }); prefs.reload(); }); })}
          style={sx('display:inline-flex;align-items:center;width:36px;height:20px;flex:0 0 36px;border-radius:11px;padding:2px;text-decoration:none;box-sizing:border-box;' + (quietOn ? 'background:#2F5C97;justify-content:flex-end;' : 'background:rgba(34,39,46,.16);justify-content:flex-start;'))}
        >
          <span style={sx('display:block;width:16px;height:16px;border-radius:50%;background:#FCFAF4;')} />
        </a>
        <span style={sx('font-size:16px;font-weight:600;')}>Quiet hours</span>
        <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:16px;color:#54606C;text-wrap:pretty;')}>
          {I(!prefs.data
            ? notServed(prefs.error) ? plain('Quiet hours and the SMS fallback are not served by the back end yet.') : '–'
            : quietOn ? 'Between ' + fmtHour(prefs.data.quietFrom) + ' and ' + fmtHour(prefs.data.quietTo) + ' only Critical events break through. Everything else waits for the ' + fmtHour(prefs.data.quietTo) + ' digest.'
            : 'Quiet hours are off, so a Medium or Info event can wake you at 3am.')}
        </span>
        <span style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
          <span style={sx('font-size:16px;font-weight:600;')}>{'Push not opened → SMS after'}</span>
          {([2, 5, 10] as const).map((m) => (
            <a
              key={m}
              href="#"
              aria-pressed={fbMin === m}
              {...wr(() => { if (prefs.data) void run(async () => { await notifPrefsSave(client, { body: { smsFallbackMinutes: m } }); prefs.reload(); }); })}
              style={sx('display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:16px;text-decoration:none;white-space:nowrap;' + (fbMin === m && prefs.data ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#F7F3EA;color:#3C454F;border:1px solid rgba(34,39,46,.14);'))}
            >
              {I(m + ' min')}
            </a>
          ))}
        </span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;margin-top:10px;text-wrap:pretty;')}>
        {I('Push reaches desktop browsers, the iPhone, iPad and Android apps, and the web console once it is on a phone’s Home Screen. SMS goes out through Twilio. Every alert and every delivery attempt is logged.')}
      </div>
    </div>
  );
}

/** "21:00" -> "9pm", "07:00" -> "7am". */
function fmtHour(hm: string): string {
  const [h, m] = hm.split(':').map(Number);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return h12 + (m ? ':' + String(m).padStart(2, '0') : '') + (h < 12 ? 'am' : 'pm');
}
